import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API, api, errMsg, money } from "@/lib/api";
import { Loading } from "@/components/Loading";

const lastMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const Item = ({ b, i, reload }) => {
  const [ref, setRef] = useState("");
  const pay = async () => {
    try { await api.post(`/admin/payouts/${b.batch_id}/items/${i.affiliate_id}/paid`, { reference: ref }); toast.success("Marked paid"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-[#E6DEC6]" data-testid={`payout-item-${b.batch_id}-${i.code}`}>
      <td className="px-3 py-3">{i.name} ({i.code})<br /><span className="text-sm">{i.payout_method || i.email}</span></td>
      <td className="px-3 py-3">{money(i.amount)}</td>
      <td className="px-3 py-3">{i.commission_ids.length}</td>
      <td className="px-3 py-3 font-semibold">{i.status}</td>
      <td className="px-3 py-3">
        {i.status === "paid" ? <span className="text-sm">{i.payment_reference}</span> : (
          <div className="flex gap-2">
            <input className="field !min-h-0 !py-1 !text-base" placeholder="Payment reference" value={ref} onChange={(e) => setRef(e.target.value)} data-testid={`payout-ref-input-${i.code}`} />
            <button className="btn-outline !min-h-0 !py-1" disabled={!ref.trim()} onClick={pay} data-testid={`payout-mark-paid-${i.code}`}>Mark paid</button>
          </div>
        )}
      </td>
    </tr>
  );
};

export const AdminPayouts = () => {
  const [batches, setBatches] = useState(null);
  const [period, setPeriod] = useState(lastMonth());
  const load = useCallback(() => api.get("/admin/payouts").then(({ data }) => setBatches(data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { await api.post("/admin/payouts", { period }); toast.success("Payout batch created"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!batches) return <Loading />;
  return (
    <div className="space-y-6" data-testid="admin-payouts">
      <div className="paper-card flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
        <div><label className="label" htmlFor="period">Payout month</label><input id="period" type="month" className="field !min-h-0 !py-2" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="payout-period-input" /></div>
        <button className="btn-primary !min-h-0 !py-2" onClick={create} data-testid="payout-create-button">Create monthly batch</button>
        <p className="text-sm text-[#5C4A3E]">Includes commissions past their 30-day hold. Balances under $25 carry forward.</p>
      </div>
      {batches.map((b) => (
        <div key={b.batch_id} className="paper-card overflow-x-auto p-5" data-testid={`payout-batch-${b.period}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-serif text-2xl font-bold">{b.period} · {money(b.total)} · <span className="text-base font-semibold">{b.status}</span></p>
            <a className="btn-outline !min-h-0 !py-1" href={`${API}/admin/payouts/${b.batch_id}/csv`} data-testid={`payout-csv-${b.period}`}>Download CSV</a>
          </div>
          <table className="mt-4 w-full text-left text-base">
            <thead className="bg-[#F2EBE1]"><tr>{["Affiliate", "Amount", "Commissions", "Status", "Payment"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
            <tbody>{b.items.map((i) => <Item key={i.affiliate_id} b={b} i={i} reload={load} />)}</tbody>
          </table>
          {!!b.carried_forward.length && <p className="mt-3 text-sm text-[#5C4A3E]">Carried forward (under $25): {b.carried_forward.map((c) => `${c.code} ${money(c.amount)}`).join(", ")}</p>}
        </div>
      ))}
      {!batches.length && <p className="text-[#5C4A3E]">No payout batches yet.</p>}
    </div>
  );
};
